import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import FieldInput from "@/components/admin/crud/FieldInput";
import RefPicker from "@/components/admin/crud/RefPicker";
import { GROUPS, humanize, singular } from "@/components/admin/crud/crudUtils";

const base = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#071b22] outline-none transition-[border-color,box-shadow] focus:border-[#165a71] focus:ring-4 focus:ring-[#165a71]/10";
const KIND = { text: "text", long: "long", number: "number", bool: "bool", date: "date", media: "media", tags: "tags" };
const isEmpty = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);

function CreateField({ spec, value, onChange }) {
  const label = spec.label || humanize(spec.key);
  const testid = `create-${spec.key}`;
  const head = (
    <label className="text-[13px] font-medium text-[#071b22]">{label}{spec.required && <span className="text-rose-500"> *</span>}</label>
  );
  const help = spec.help && <p className="mt-1 text-xs text-slate-400">{spec.help}</p>;
  if (spec.type === "ref" || spec.type === "refs") return (
    <div className={spec.type === "refs" ? "md:col-span-2" : ""}>{head}<RefPicker entity={spec.ref} value={value} onChange={onChange} multi={spec.type === "refs"} testid={testid} />{help}</div>
  );
  if (spec.type === "select") return (
    <div>{head}
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} className={`${base} capitalize`} data-testid={testid}>
        {!spec.required && <option value="">—</option>}
        {spec.required && isEmpty(value) && <option value="">Choose…</option>}
        {(spec.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>{help}
    </div>
  );
  if (spec.type === "money") return (
    <div>{head}
      <div className="relative"><span className="absolute left-3.5 top-1/2 -translate-y-1/2 mt-0.5 text-sm text-slate-400">$</span>
        <input type="number" min="0" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={`${base} pl-7`} data-testid={testid} />
      </div>{help}
    </div>
  );
  const kind = KIND[spec.type] || "text";
  return (
    <div className={kind === "long" || kind === "media" ? "md:col-span-2" : ""}>
      <FieldInput field={spec.key} label={label} kind={kind} value={value} onChange={onChange} required={spec.required} testid={testid} />{help}
    </div>
  );
}

export default function AdminCRUDCreatePage() {
  const { entity } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [fields, setFields] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/crud/entities").then(({ data }) => setMeta((data || []).find((e) => e.key === entity) || false));
    api.get(`/admin/crud/${entity}/blueprint`).then(({ data }) => {
      setFields(data.fields);
      const init = {};
      data.fields.forEach((f) => { if (f.default != null) init[f.key] = f.default; });
      setForm((prev) => ({ ...init, ...prev }));
    }).catch((err) => { toast.error(err?.response?.data?.error || "This type can't be created here"); setFields(false); });
  }, [entity]);

  const grouped = useMemo(() => {
    const g = {};
    (fields || []).forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [fields]);
  const required = (fields || []).filter((f) => f.required);
  const missing = required.filter((f) => isEmpty(form[f.key]));

  const create = async () => {
    if (missing.length) { toast.error(`Please fill in: ${missing.map((f) => f.label || humanize(f.key)).join(", ")}`); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/crud/${entity}`, form);
      toast.success(`${singular(meta.label)} created`);
      navigate(`/admin/entities/${entity}/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.error || err?.response?.data?.detail || "Create failed"); } finally { setSaving(false); }
  };

  if (meta === null || fields === null) return <div className="p-10 text-slate-500" data-testid="crud-create-loading">Loading…</div>;
  if (!meta || !fields) return <div className="p-10 text-slate-500" data-testid="crud-create-unavailable">New records of this type are created by the platform. <Link to={`/admin/entities/${entity}`} className="text-[#165a71] underline">Back to list</Link></div>;
  const one = singular(meta.label);

  return (
    <div className="max-w-6xl pb-16" data-testid="crud-create-page">
      <Link to={`/admin/entities/${entity}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#165a71] transition-colors" data-testid="crud-create-back"><ArrowLeft className="h-4 w-4" /> All {meta.label.toLowerCase()}</Link>
      <div className="mt-4 mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">{meta.group}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">New {one.toLowerCase()}</h1>
          {entity === "provider_profiles" && <p className="mt-2 text-sm text-slate-500 max-w-xl">Creates a listing without a login. The real business can claim it later from the profile page.</p>}
        </div>
        <button onClick={create} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:bg-[#0f4657] disabled:opacity-50 transition-colors" data-testid="crud-create-submit"><Plus className="h-4 w-4" /> {saving ? "Creating…" : `Create ${one.toLowerCase()}`}</button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] items-start">
        <div className="space-y-6">
          {GROUPS.filter((g) => grouped[g.key]?.length).map((g) => (
            <section key={g.key} className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-7 shadow-[0_1px_2px_rgba(7,27,34,0.04)]" data-testid={`create-section-${g.key}`}>
              <h2 className="font-display text-lg font-semibold text-[#071b22]">{g.key === "advanced" ? "Links & references" : g.label}</h2>
              <p className="text-sm text-slate-500 mt-0.5 mb-5">{g.key === "advanced" ? "Connect this record to others." : g.hint}</p>
              <div className="grid gap-5 md:grid-cols-2">
                {grouped[g.key].map((f) => <CreateField key={f.key} spec={f} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} />)}
              </div>
            </section>
          ))}
        </div>
        <aside className="lg:sticky lg:top-6 rounded-3xl border border-slate-200/80 bg-white p-5" data-testid="crud-create-checklist">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-3">Required</div>
          <ul className="space-y-2">
            {required.map((f) => (
              <li key={f.key} className="flex items-center gap-2 text-sm">
                {isEmpty(form[f.key]) ? <Circle className="h-4 w-4 text-slate-300" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                <span className={isEmpty(form[f.key]) ? "text-slate-600" : "text-slate-400 line-through"}>{f.label || humanize(f.key)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400">You can add everything else after creating, in the full editor.</p>
        </aside>
      </div>
    </div>
  );
}
